import React from "react";
import AnalysisSection from "./components/AnalysisSection";
import AnalysisDetail from "./components/AnalysisDetail";
import {
  Alert,
  AlertDescription,
} from "../../../../components/ui/AlertDetailedReport";

import detailedAnalysisIcon from "../../../../assets/images/detailed_analysis.svg";
import improvementIcon from "../../../../assets/images/information.svg";
import {
  StatusLevel,
  FinalRecommendation as ComponentFinalRecommendation,
} from "../../types";
// or whichever icon you want for improvements

interface FinalRecommendationProps {
  data: {
    structureAndEvidenceBasedArgumentation: {
      detailedAnalysis: string;
      candidateProvidedRecommendation: string;
      improvementSuggestions: string[]; // <-- array of strings
      status: StatusLevel;
    };
    riskConsideration: {
      detailedAnalysis: string;
      identifiedRisks: string;
      otherRisks: string[]; // <-- array of strings
      status: StatusLevel;
    };
    nextSteps: {
      detailedAnalysis: string;
      candidateProvidedNextSteps: string;
      additionalImprovementSuggestions: string[]; // <-- array of strings
      status: StatusLevel;
    };
    topCandidateResponse: string;
  };
  componentAnalysisSection?: ComponentFinalRecommendation;
}

const FinalRecommendation: React.FC<FinalRecommendationProps> = ({
  data,
  componentAnalysisSection,
}) => {
  const {
    structureAndEvidenceBasedArgumentation,
    riskConsideration,
    nextSteps,
    topCandidateResponse,
  } = data;

  return (
    <div className="space-y-6">
      {/****************************************************************
        1) Structure & Evidence-Based Argumentation
       ****************************************************************/}
      <AnalysisSection
        title="Structure and Evidence"
        status={
          structureAndEvidenceBasedArgumentation.status ||
          componentAnalysisSection?.criteria.structure_and_evidence.status ||
          componentAnalysisSection?.overallStatus
        }
      >
        {/* Detailed Analysis */}
        <AnalysisDetail
          icon={<img src={detailedAnalysisIcon} alt="Detailed Analysis" />}
          title="Detailed analysis"
        >
          {structureAndEvidenceBasedArgumentation.detailedAnalysis}
        </AnalysisDetail>

        {/* Candidate Recommendation & Improvement Suggestions side by side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Candidate Recommendation */}
          <div>
            <h4 className="font-semibold text-[#0EBC8B] mb-2">
              Candidate Recommendation
            </h4>
            <Alert className="!border-0 !shadow-none">
              <AlertDescription className="!text-base text-gray-700">
                {
                  structureAndEvidenceBasedArgumentation.candidateProvidedRecommendation
                }
              </AlertDescription>
            </Alert>
          </div>

          {/* Improvement Suggestions (array) */}
          {structureAndEvidenceBasedArgumentation.improvementSuggestions
            .length > 0 && (
            <div>
              <h4 className="font-semibold text-[#EE0000] mb-2">
                Improvement Suggestions
              </h4>
              <Alert className="!border-0 !shadow-none">
                <AlertDescription className="!text-base text-gray-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {structureAndEvidenceBasedArgumentation.improvementSuggestions.map(
                      (item, idx) => (
                        <li key={idx}>{item}</li>
                      )
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </AnalysisSection>

      {/****************************************************************
        2) Risk Consideration
       ****************************************************************/}
      <AnalysisSection
        title="Risk Consideration"
        status={
          riskConsideration.status ||
          componentAnalysisSection?.criteria.risk_consideration.status ||
          componentAnalysisSection?.overallStatus
        }
      >
        {/* Detailed Analysis */}
        <AnalysisDetail
          icon={<img src={detailedAnalysisIcon} alt="Detailed Analysis" />}
          title="Detailed analysis"
        >
          {riskConsideration.detailedAnalysis}
        </AnalysisDetail>

        {/* Identified Risks & Other Risks side by side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Identified Risks */}
          <div>
            <h4 className="font-semibold text-[#0EBC8B] mb-2">
              Identified Risks
            </h4>
            <Alert className="!border-0 !shadow-none text-gray-700">
              <AlertDescription className="!text-base">
                {riskConsideration.identifiedRisks}
              </AlertDescription>
            </Alert>
          </div>

          {/* Other Risks (array) */}
          {riskConsideration.otherRisks.length > 0 && (
            <div>
              <h4 className="font-semibold text-[#EE0000] mb-2">Other Risks</h4>
              <Alert className="!border-0 !shadow-none">
                <AlertDescription className="!text-base text-gray-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {riskConsideration.otherRisks.map((risk, idx) => (
                      <li key={idx}>{risk}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </AnalysisSection>

      {/****************************************************************
        3) Next Steps
       ****************************************************************/}
      <AnalysisSection
        title="Next Steps"
        status={
          nextSteps.status ||
          componentAnalysisSection?.criteria.next_steps.status ||
          componentAnalysisSection?.overallStatus
        }
      >
        {/* Detailed Analysis */}
        <AnalysisDetail
          icon={<img src={detailedAnalysisIcon} alt="Detailed Analysis" />}
          title="Detailed analysis"
        >
          {nextSteps.detailedAnalysis}
        </AnalysisDetail>

        {/* Candidate Provided Next Steps & Additional Suggestions side by side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Candidate Provided Next Steps */}
          <div>
            <h4 className="font-semibold text-[#0EBC8B] mb-2">
              Candidate Provided Next Steps
            </h4>
            <Alert className="!border-0 !shadow-none">
              <AlertDescription className="!text-base text-gray-700">
                {nextSteps.candidateProvidedNextSteps}
              </AlertDescription>
            </Alert>
          </div>

          {/* Additional Improvement Suggestions (array) */}
          {nextSteps.additionalImprovementSuggestions.length > 0 && (
            <div>
              <h4 className="font-semibold text-[#EE0000] mb-2">
                Additional Suggestions
              </h4>
              <Alert className="!border-0 !shadow-none">
                <AlertDescription className="!text-base text-gray-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {nextSteps.additionalImprovementSuggestions.map(
                      (suggestion, idx) => (
                        <li key={idx}>{suggestion}</li>
                      )
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </AnalysisSection>

      {/* {topCandidateResponse && (
        <AnalysisSection title="Top Candidate Response">
          <Alert className="!border-0 !shadow-none">
            <AlertDescription className="!text-base text-gray-700">
              {topCandidateResponse}
            </AlertDescription>
          </Alert>
        </AnalysisSection>
      )} */}
    </div>
  );
};

export default FinalRecommendation;
